import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import authService from '../../services/authService';
import { showError, showSuccess } from '../../utils/toast';

const LoginPage = () => {
    const navigate = useNavigate();
    const { login, user, isAuthenticated } = useAuth();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Redirect if already logged in
    useEffect(() => {
        if (isAuthenticated()) {
            if (user?.role === 'admin') {
                navigate('/admin/dashboard', { replace: true });
            } else {
                navigate('/dashboard', { replace: true });
            }
        }
    }, [user, isAuthenticated, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        console.log('Validation errors:', newErrors);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        e.stopPropagation();

        console.log('Form submitted, validating...');

        if (!validateForm()) {
            console.log('Validation failed, stopping submission');
            return false;
        }

        setIsLoading(true);

        try {
            // Call backend API
            const response = await authService.login(formData.email, formData.password);

            if (response.success) {
                // Store tokens and user data
                login(
                    response.user,
                    response.access_token,
                    response.refresh_token
                );

                showSuccess(`Welcome back, ${response.user.full_name}!`);

                // Redirect based on role
                if (response.user.role === 'admin') {
                    navigate('/admin/dashboard');
                } else {
                    navigate('/dashboard');
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || 'Login failed. Please check your credentials.';
            console.log('Login error:', errorMessage, error);
            showError(errorMessage);
            setErrors({ submit: errorMessage });
        } finally {
            setIsLoading(false);
        }

        return false;
    };

    return (
        <div className="bg-background-light dark:bg-background-dark min-h-screen flex flex-col">
            {/* Top Navigation Bar */}
            <header className="flex items-center justify-between whitespace-nowrap border-b border-solid border-[#dfe0e2] dark:border-[#333] px-6 md:px-10 py-3 bg-white dark:bg-background-dark">
                <div className="flex items-center gap-4">
                    <Link to="/" className="text-gray-500 hover:text-primary transition-colors" title="Back to Home">
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </Link>
                    <Link to="/" className="flex items-center gap-4 text-primary group">
                        <div className="h-10 w-auto group-hover:scale-105 transition-transform">
                            <img
                                src="https://upload.wikimedia.org/wikipedia/commons/thumb/c/c0/COMSATS_new_logo.jpg/770px-COMSATS_new_logo.jpg"
                                alt="University Portal"
                                className="h-full w-auto object-contain"
                            />
                        </div>
                        <h2 className="text-[#131416] dark:text-white text-lg font-bold leading-tight tracking-[-0.015em] group-hover:text-primary transition-colors">
                            University Portal
                        </h2>
                    </Link>
                </div>
                <div className="flex items-center gap-4">
                    <Link to="/" className="hidden sm:block text-sm font-medium text-gray-500 hover:text-primary transition-colors">
                        Back to Home
                    </Link>
                </div>
            </header>

            {/* Main Content */}
            <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
                <div className="w-full max-w-[480px]">
                    {/* Headline Section */}
                    <div className="text-center mb-8">
                        <h1 className="text-[#131416] dark:text-white tracking-tight text-[32px] font-bold leading-tight mb-2">
                            Student & Staff Login
                        </h1>
                        <p className="text-[#6e757c] dark:text-gray-400 text-base font-normal">
                            Access your courses, grades, and resources
                        </p>
                    </div>

                    {/* Login Card */}
                    <div className="bg-white dark:bg-background-dark/50 border border-transparent shadow-[0_0_20px_rgba(0,0,0,0.05)] rounded-xl overflow-hidden p-8">
                        <div className="mb-6 flex justify-center">
                            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="material-symbols-outlined text-primary text-4xl">lock</span>
                            </div>
                        </div>

                        {errors.submit && (
                            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                                {errors.submit}
                            </div>
                        )}

                        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
                            {/* Email Field */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-medium">
                                    Email Address
                                </label>
                                <div className="relative">
                                    <input
                                        className={`w-full rounded-lg text-[#131416] dark:text-white border ${errors.email ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                            } bg-white dark:bg-background-dark h-12 px-4 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all`}
                                        placeholder="your.email@gmail.com"
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                    />
                                </div>
                                {errors.email && (
                                    <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                                )}
                            </div>

                            {/* Password Field */}
                            <div className="flex flex-col gap-2">
                                <label className="text-[#131416] dark:text-white text-sm font-medium">
                                    Password
                                </label>
                                <div className="flex items-stretch">
                                    <input
                                        className={`flex-1 rounded-l-lg text-[#131416] dark:text-white border ${errors.password ? 'border-red-500' : 'border-[#dfe0e2] dark:border-gray-700'
                                            } bg-white dark:bg-background-dark h-12 px-4 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all`}
                                        placeholder="••••••••"
                                        type={showPassword ? 'text' : 'password'}
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                    />
                                    <div className="flex items-center justify-center px-3 border border-l-0 border-[#dfe0e2] dark:border-gray-700 bg-white dark:bg-background-dark rounded-r-lg text-gray-400">
                                        <span
                                            className="material-symbols-outlined text-xl cursor-pointer hover:text-primary"
                                            onClick={() => setShowPassword(!showPassword)}
                                        >
                                            {showPassword ? 'visibility_off' : 'visibility'}
                                        </span>
                                    </div>
                                </div>
                                {errors.password && (
                                    <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                                )}
                            </div>

                            {/* Submit Button */}
                            <button
                                className="w-full bg-primary hover:bg-primary/90 text-white font-bold py-3 px-4 rounded-lg transition-colors mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                                type="submit"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Logging in...' : 'Login to Portal'}
                            </button>
                        </form>

                        {/* Signup Link */}
                        <div className="mt-8 pt-6 border-t border-[#f1f2f3] dark:border-gray-800 text-center">
                            <p className="text-[#6e757c] dark:text-gray-400 text-sm">
                                Don't have an account?{' '}
                                <Link to="/signup" className="text-primary font-bold hover:underline ml-1">
                                    Sign Up
                                </Link>
                            </p>
                        </div>
                    </div>

                    {/* Security Notice */}
                    <div className="mt-8 flex items-center justify-center gap-2 text-[#6e757c] dark:text-gray-500 text-xs">
                        <span className="material-symbols-outlined text-sm">verified_user</span>
                        <span>Secure 256-bit SSL Encrypted Connection</span>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="py-8 px-6 text-center text-[#6e757c] dark:text-gray-500 text-sm">
                <p>© 2024 University Academic Portal. All Rights Reserved.</p>
                <div className="flex justify-center gap-4 mt-2">
                    <button type="button" onClick={(e) => e.preventDefault()} className="hover:text-primary">Privacy Policy</button>
                    <button type="button" onClick={(e) => e.preventDefault()} className="hover:text-primary">Terms of Service</button>
                    <button type="button" onClick={(e) => e.preventDefault()} className="hover:text-primary">Contact IT Support</button>
                </div>
            </footer>
        </div>
    );
};

export default LoginPage;
